import React, { useState, useEffect, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { 
  HiOutlineArrowLeft, HiOutlineCheckCircle, HiOutlineShieldCheck, 
  HiOutlineSparkles, HiOutlineCalendar
} from "react-icons/hi2";
import { MdOutlineSupportAgent, MdOutlineVerifiedUser } from "react-icons/md";
import { baseurl, imgurl } from "../baseurl";
import Swal from "sweetalert2";
import "../asset/css/detail.css";

const PackageDetail = () => {
  const { id } = useParams();
  const [pkg, setPkg] = useState(null);
  const [loading, setLoading] = useState(true);

  // Refs for Flatpickr
  const departureRef = useRef(null);
  const returnRef = useRef(null);

  const [formData, setFormData] = useState({
    full_name: "",
    email: "",
    phone: "",
    departure_date: "",
    return_date: "",
    package_id: id
  });

  useEffect(() => {
    const fetchPackageDetail = async () => {
      try {
        const response = await axios.get(`${baseurl}/getPackageById/${id}`);
        if (response.data.status === "success") {
          setPkg(response.data.data);
          setFormData(prev => ({ ...prev, package_id: id }));
        }
      } catch (error) {
        console.error("Error fetching package details:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchPackageDetail();
    window.scrollTo(0, 0);
  }, [id]);

  // Initialize Flatpickr
  useEffect(() => {
    if (!loading && window.flatpickr) {
      const departurePicker = window.flatpickr(departureRef.current, {
        dateFormat: "Y-m-d",
        minDate: "today",
        onChange: (selectedDates, dateStr) => {
          setFormData(prev => ({ ...prev, departure_date: dateStr }));
          // Set minDate for return picker to the selected departure date
          returnPicker.set('minDate', dateStr);
        },
      });

      const returnPicker = window.flatpickr(returnRef.current, {
        dateFormat: "Y-m-d",
        minDate: "today",
        onChange: (selectedDates, dateStr) => {
          setFormData(prev => ({ ...prev, return_date: dateStr }));
        },
      });
    }
  }, [loading]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleBooking = async (e) => {
    e.preventDefault();
    if (!formData.departure_date || !formData.return_date) {
      Swal.fire("Info", "Please select travel dates", "info");
      return;
    }
    
    try {
      const response = await axios.post(`${baseurl}/bookPackage`, formData);
      if (response.data.status === "success") {
        Swal.fire({
          icon: 'success',
          title: 'Booking Request Sent! Team Will Contact You Soon',
          text: 'Our travel expert will contact you within 24 hours.',
          confirmButtonColor: '#166070'
        });
        setFormData({
          full_name: "", email: "", phone: "",
          departure_date: "", return_date: "", package_id: id
        });
      }
    } catch (error) {
      Swal.fire({ icon: 'error', title: 'Error', text: 'Service temporarily unavailable.' });
    }
  };

  if (loading) return <div className="loader-container"><div className="spinner-border text-teal"></div></div>;
  if (!pkg) return <div className="text-center py-5">Package not found.</div>;

  return (
    <div className="detail-page bg-light">
      <section className="premium-hero" style={{ 
        backgroundImage: `linear-gradient(to bottom, rgba(0,0,0,0.2), rgba(0,0,0,0.8)), url(${imgurl}/uploads/${pkg.banner_path})` 
      }}>
        <div className="container h-100 position-relative pt-2">
          <div className="hero-top-nav pt-2 mt-5">
            {/* <Link to="/packages" className="glass-back-btn">
              <HiOutlineArrowLeft /> Back to Packages
            </Link> */}
          </div>
          <div className="hero-main-content animate-fade-up">
            <span className="premium-badge"><HiOutlineSparkles className="me-2"/> Premium Collection</span>
            <h1 className="display-2 fw-bold text-white mb-3">{pkg.package_name}</h1>
            {/* <div className="glass-info-bar d-flex flex-wrap gap-4 p-4 mt-4">
               <div className="info-item">
                <p className="small text-white-50 mb-0">Price starting from</p>
                <h6 className="text-yellow mb-0 fs-4">₹{parseFloat(pkg.price).toLocaleString()}</h6>
              </div>
            </div> */}
          </div>
        </div>
      </section>

      <div className="container py-5">
        <div className="row g-5">
          <div className="col-lg-7">
            <div className="content-card mb-5">
              <h2 className="section-title mb-4">The Experience</h2>
              <p className="lead text-muted mb-4">{pkg.package_description}</p>
            </div>
          </div>

          <div className="col-lg-5">
            <div className="sticky-form-container">
              <div className="modern-booking-card shadow-lg overflow-hidden">
                <div className="card-accent-top"></div>
                <div className="p-4 p-xl-5">
                  <div className="text-center mb-4">
                    <h3 className="fw-bold">Reserve Your Trip</h3>
                    <p className="text-muted">Personalized itinerary booking</p>
                  </div>

                  <form onSubmit={handleBooking} className="booking-form-minimal">
                    <div className="mb-3">
                      <input type="text" name="full_name" required placeholder="Full Name" 
                        value={formData.full_name} onChange={handleInputChange} className="modern-input" />
                    </div>
                    
                    <div className="mb-3">
                      <input type="email" name="email" required placeholder="Email Address" 
                        value={formData.email} onChange={handleInputChange} className="modern-input" />
                    </div>

                    <div className="mb-3">
                      <input type="tel" name="phone" required placeholder="Phone Number" 
                        value={formData.phone} onChange={handleInputChange} className="modern-input" />
                    </div>

                    <div className="row g-3 mb-4">
                      <div className="col-6">
                        <label className="xs-label">Departure</label>
                        <div className="datepicker-wrapper">
                          <input 
                            ref={departureRef}
                            type="text" 
                            placeholder="Select Date" 
                            className="modern-input" 
                            readOnly 
                          />
                          <HiOutlineCalendar className="dp-icon" />
                        </div>
                      </div>
                      <div className="col-6">
                        <label className="xs-label">Return</label>
                        <div className="datepicker-wrapper">
                          <input 
                            ref={returnRef}
                            type="text" 
                            placeholder="Select Date" 
                            className="modern-input" 
                            readOnly 
                          />
                          <HiOutlineCalendar className="dp-icon" />
                        </div>
                      </div>
                    </div>

                    <button type="submit" className="btn-luxury-action w-100 py-3">
                      SEND BOOKING REQUEST
                    </button>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PackageDetail;